import { useEffect, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Save, X } from 'lucide-react';
import { api, type ApiError } from '../../api';
import type { Presentation } from '../../../shared/types';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';

type Form = {
  id?: string;
  code: string;
  type: 'oral' | 'poster';
  title: string;
  presenterName: string;
  category: string;
  timeSlot: string;
  boardNumber: string;
  abstractNo: string;
  displayTime: string;
  location: string;
};
const blank: Form = {
  code: '', type: 'oral', title: '', presenterName: '', category: '',
  timeSlot: '', boardNumber: '', abstractNo: '', displayTime: '', location: '',
};

export default function ManagePresentations({ yearId, onChange }: { yearId: number; onChange: () => void }) {
  const [items, setItems] = useState<Presentation[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<Form | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(false);
  useEffect(() => {
    const open = form !== null;
    if (open && !wasOpen.current) formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    wasOpen.current = open;
  }, [form]);

  async function load() {
    setLoading(true);
    try {
      const d = await api<{ presentations: Presentation[] }>(`admin-presentations?yearId=${yearId}`);
      setItems(d.presentations);
    } catch (e) {
      toast.error((e as ApiError).message || 'Failed to load presentations');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, [yearId]);

  const set = (patch: Partial<Form>) => setForm((f) => (f ? { ...f, ...patch } : f));

  async function save() {
    if (!form) return;
    if (!form.title.trim()) return toast.error('Title is required');
    setBusy(true);
    try {
      await api('admin-presentations', {
        body: { action: form.id ? 'update' : 'create', yearId, ...form },
      });
      toast.success(form.id ? 'Presentation updated' : 'Presentation added');
      setForm(null);
      await load();
      onChange();
    } catch (e) {
      toast.error((e as ApiError).message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function remove(p: Presentation) {
    if (!confirm(`Delete "${p.title}"? This also removes its allocations and any scores.`)) return;
    try {
      await api('admin-presentations', { body: { action: 'delete', id: p.id } });
      toast.success('Deleted');
      await load();
      onChange();
    } catch (e) {
      toast.error((e as ApiError).message || 'Delete failed');
    }
  }

  function edit(p: Presentation) {
    setForm({
      id: p.id,
      code: p.code ?? '',
      type: p.type,
      title: p.title,
      presenterName: p.presenterName ?? '',
      category: p.category ?? '',
      timeSlot: p.timeSlot ?? '',
      boardNumber: p.boardNumber ?? '',
      abstractNo: p.abstractNo ?? '',
      displayTime: p.displayTime ?? '',
      location: p.location ?? '',
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{items.length} presentations</p>
        {!form && (
          <Button size="sm" onClick={() => setForm({ ...blank })}>
            <Plus /> Add presentation
          </Button>
        )}
      </div>

      {form && (
        <Card ref={formRef} className="scroll-mt-20 space-y-3 p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{form.id ? 'Edit presentation' : 'New presentation'}</h3>
            <Button size="icon" variant="ghost" onClick={() => setForm(null)} aria-label="Close">
              <X />
            </Button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Code"><Input value={form.code} onChange={(e) => set({ code: e.target.value })} placeholder="OP019 / PP041" /></Field>
            <Field label="Type">
              <select value={form.type} onChange={(e) => set({ type: e.target.value as Form['type'] })} className="h-11 w-full rounded-md border border-input bg-card px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <option value="oral">Oral</option>
                <option value="poster">Poster</option>
              </select>
            </Field>
          </div>
          <Field label="Title"><Input value={form.title} onChange={(e) => set({ title: e.target.value })} /></Field>
          <Field label="Presenter"><Input value={form.presenterName} onChange={(e) => set({ presenterName: e.target.value })} /></Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Category"><Input value={form.category} onChange={(e) => set({ category: e.target.value })} placeholder="Research / Clinical Practice…" /></Field>
            {form.type === 'oral' ? (
              <Field label="Time slot"><Input value={form.timeSlot} onChange={(e) => set({ timeSlot: e.target.value })} placeholder="Sunday Session 3" /></Field>
            ) : (
              <Field label="Board row"><Input value={form.boardNumber} onChange={(e) => set({ boardNumber: e.target.value })} /></Field>
            )}
          </div>
          {form.type === 'poster' && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Abstract no."><Input value={form.abstractNo} onChange={(e) => set({ abstractNo: e.target.value })} /></Field>
              <Field label="Display time"><Input value={form.displayTime} onChange={(e) => set({ displayTime: e.target.value })} placeholder="Saturday 1.40pm" /></Field>
            </div>
          )}
          <Field label="Location"><Input value={form.location} onChange={(e) => set({ location: e.target.value })} placeholder="e.g. Promenade, poster 7" /></Field>
          <div className="flex gap-2">
            <Button onClick={save} disabled={busy}>
              <Save /> {form.id ? 'Save changes' : 'Add presentation'}
            </Button>
            <Button variant="ghost" onClick={() => setForm(null)}>Cancel</Button>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full rounded-lg" />
          <Skeleton className="h-16 w-full rounded-lg" />
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((p) => (
            <Card key={p.id} className="flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-muted-foreground">{p.code ?? p.id}</span>
                  <Badge variant={p.type === 'oral' ? 'oral' : 'poster'} className="capitalize">{p.type}</Badge>
                  {p.category && <Badge variant="secondary" className="hidden sm:inline-flex">{p.category}</Badge>}
                </div>
                <div className="line-clamp-1 text-sm font-medium">{p.title}</div>
                <div className="line-clamp-1 text-xs text-muted-foreground">{p.presenterName}</div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => edit(p)} aria-label="Edit"><Pencil /></Button>
              <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => remove(p)} aria-label="Delete"><Trash2 /></Button>
            </Card>
          ))}
          {items.length === 0 && <p className="py-8 text-center text-muted-foreground">No presentations yet. Add the first one.</p>}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
